import * as React from "react";
import { useState, useMemo,useEffect } from "react";
import { parseAndSanitizeMarkdown } from "../utils/markdownParser";
import { insertContentIntoWord } from "../utils/officeUtils";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import FeedbackActions from "./FeedbackActions";
import FeedbackModal from "./FeedbackModal";
import AvailableFilesMessage from "./AvailableFilesMessage";
import "./StyleSheet.css";
import googleIcon from '../assets/google-icon.png';
import logger from "../utils/logger.jsx";
import SqlTableMessage from "./SqlTableMessage";
import { Avatar, Spinner } from "@fluentui/react-components";
import BookmarkAction from "./BookmarkAction";


/**
 * Component: GoogleSuggestions
 * ----------------------------------------------------------------------
 * Renders a horizontal scrollable list of Google Search suggestion chips.
 * Returns null if no valid links are provided.
 *
 * @param {Object} props - Component properties
 * @param {} props.links - Array of objects containing { text, url }
 */
const GoogleSuggestions = ({ links }) => {
  // If there are no links, render nothing.
  if (!links || links.length === 0) {
    return null;
  }

  const hasValidLinks = links.some(link => link && link.text && link.url);
  if (!hasValidLinks) {
    return null;
  }

  return (
    <>
      <div className="suggestions-header">
        <img src={googleIcon} alt="Google" />
        <span>Google Search Suggestions</span>
      </div>
      <div className="suggestions-chips">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="suggestion-chip"
          >
            {link.text}
          </a>
        ))}
      </div>
    </>
  );
};

GoogleSuggestions.propTypes = {
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
  })),
};

/**
 * Component: DocumentSources
 * ----------------------------------------------------------------------
 * Renders a list of reference links (citations) indicating where the
 * information in the response was sourced from.
 *
 * @param {Object} props
 * @param {Array<string>} props.fileNames - Display names for the files
 * @param {Array<string>} props.urls - Corresponding URLs for the files
 */
const DocumentSources = ({ fileNames, urls, pageNumbers }) => {
  if (!fileNames || fileNames.length === 0 || !urls || urls.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '16px' }}>
      {/* References Label Added Here */}
      <span style={{
        fontWeight: '600',
        fontSize: '0.95rem',
        color: '#323130',
        marginBottom: '8px',
        display: 'inline-block'
      }}>
        References:
      </span>
      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: "250px", overflow: "auto", scrollbarWidth: "thin", scrollbarColor: "rgb(189, 227, 255) rgb(255, 255, 255)" }}>
        {fileNames.map((name, index) => {
          // Ensure there is a URL for this file
          if (!urls[index]) return null;
          const pageNum = pageNumbers && pageNumbers[index];
          // Construct the display text: "Filename , page X" or just "Filename"
          const linkText = pageNum ? `${name}, page ${pageNum}` : name;
          // Construct the URL: If page exists, append PDF open parameter #page=X
          const finalUrl = pageNum ? `${urls[index]}#page=${pageNum}` : urls[index];

          return (
            <a
              key={index}
              href={finalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="source-file-card"
              title={linkText}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: "#c9eafa",
                border: '1px solid #c9eafa',
                borderRadius: '8px',
                padding: '10px 12px',
                textDecoration: 'none',
                color: '#5090d3',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                maxWidth: '100%'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c9eafa'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#c9eafa'}
            >
              {/* SVG File Icon */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fill="#5090d3" stroke="#5090d3" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M14 2V8H20" stroke="#5090d3" strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              </div>


              {/* Unified Text Span for Filename and Page Number */}
              <span style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {linkText}
              </span>

              {/* External Link Icon */}
              <div style={{ marginLeft: '8px', opacity: 0.6, display: 'flex' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

DocumentSources.propTypes = {
  fileNames: PropTypes.arrayOf(PropTypes.string),
  urls: PropTypes.arrayOf(PropTypes.string),
  pageNumbers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])), // Added prop type

};

/**
 * Component: Main
 * ----------------------------------------------------------------------
 * The primary chat interface component. It handles:
 * 1. Rendering the list of chat messages (User, Bot, System, SQL Tables).
 * 2. Managing feedback state (Like/Dislike).
 * 3. Handling Clipboard operations (Copy).
 * 4. Interfacing with Google Apps Script for document insertion.
 *
 * @param {Object} props
 * @param {} props.messages - The history of chat messages
 * @param {boolean} props.isLoading - Global loading state
 * @param {} props.uploadedFileNames - Files currently available in context
 * @param {Function} props.onFeedbackSubmit - Callback for sending feedback to API
 * @param {} props.sessionId - Current session identifier
 * @param {Function} props.onLogCopy - Callback to log copy actions
 * @param {Function} props.onToggleBookmark - Callback to bookmark specific messages
 */
const Main = ({ messages, isLoading, uploadedFileNames, onFeedbackSubmit, sessionId, onLogCopy, onToggleBookmark, onRetry }) => {

  const [feedbackState, setFeedbackState] = useState({});
  const [isInserting, setIsInserting] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedMessageForFeedback, setSelectedMessageForFeedback] = useState(null);

  // This state will force the modal to re-mount when it changes.
  const [feedbackModalKey, setFeedbackModalKey] = useState(0);

   // Clear all feedback UI state whenever a new session starts (e.g. Chat is cleared)
     useEffect(() => {
      setFeedbackState({});
      setIsFeedbackModalOpen(false);
      setSelectedMessageForFeedback(null);
     }, [sessionId]);
  /**
* handleCopy
* Copies rendered HTML content to the clipboard.
* Special logic is applied to tables to ensure borders render correctly
* when pasted into Word/Excel/Outlook.
*
* @param {} htmlContent - The HTML string to copy
* @param {} requestLogId - ID for logging the action
*/
  const handleCopy = async (htmlContent, requestLogId) => {
    if (!navigator.clipboard || !navigator.clipboard.write) {
      toast.error("Formatted copy is not supported in this browser.", { duration: 4000 });
      return;
    }

    try {
      let styledHtmlContent = htmlContent;
      if (styledHtmlContent.includes('<table>')) {
        logger.info('Table detected in content to be copied. Applying border styles.', { functionName: 'handleCopy' });
        styledHtmlContent = styledHtmlContent.replace(
          /<table>/g,
          '<table border="1" style="border-collapse: collapse; width: 100%;">'
        );
      }

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      const htmlBlob = new Blob([styledHtmlContent], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });

      const clipboardItem = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });

      await navigator.clipboard.write([clipboardItem]);

      toast.success("Formatted response copied to clipboard!");

      onLogCopy(requestLogId);

    } catch (error) {
      console.error("Failed to copy formatted text:", error);
      toast.error("Could not copy the response.");
    }
  };

  /**
 * handleFeedback
 * Manages the UI state for Like/Dislike buttons.
 * - If 'like': Sends API request immediately.
 * - If 'dislike': Opens the detailed FeedbackModal.
 *
 * @param {} type - 'like' or 'dislike'
 * @param {number} messageIndex - Index of the message in the array
 */
  const handleFeedback = (type, messageIndex) => {
    const currentFeedback = feedbackState[messageIndex];
    console.log(`Feedback action: '${type}' for message index: ${messageIndex}. Current state: ${currentFeedback}`);

    if (type === 'like') {
      const isAlreadyLiked = currentFeedback === 'like';

      setFeedbackState(prev => ({
        ...prev,
        [messageIndex]: isAlreadyLiked ? null : 'like'
      }));

      if (!isAlreadyLiked) {
        const message = messages[messageIndex];

        if (!message.request_log_id) {
          logger.warn("Feedback aborted: request_log_id is missing.", { functionName: 'handleFeedback', messageIndex });

          toast.error("Cannot submit feedback: Message ID is missing.");
          console.warn("Feedback aborted: request_log_id is missing from message object.", message);
          return;
        }

        const payload = {
          request_log_id: message.request_log_id,
          rating: 1, // 1 for "like"
          comment: "",
          expected_response: "",
          metrics: [],
          data_source: message.type || "unknown",
          attached_files: []
        };

        logger.info('User submitted positive feedback (Like).', {
          functionName: 'handleFeedback',
          requestLogId: message.request_log_id
        });
        onFeedbackSubmit(payload);
      }
    } else if (type === 'dislike') {
      if (currentFeedback === 'dislike') {
        logger.info("Dislike feedback button clicked again, but already submitted.", { functionName: 'handleFeedback' });
        console.log("Dislike feedback already submitted. No action taken.");
        return;
      }
      logger.info('User clicked Dislike. Opening detailed feedback modal.', { functionName: 'handleFeedback', messageIndex });
      setFeedbackModalKey(Date.now());
      setSelectedMessageForFeedback(messageIndex);
      setIsFeedbackModalOpen(true);
    }
  };

  /**
 * handleCloseFeedbackModal
 * Closes the detailed feedback modal and clears the selection state.
 */
  const handleCloseFeedbackModal = () => {
    logger.info('Closing feedback modal.', { functionName: 'handleCloseFeedbackModal' });
    setIsFeedbackModalOpen(false);
    setSelectedMessageForFeedback(null);
  };

  /**
 * handleSubmitFeedback
 * Callback received from the FeedbackModal. Constructs the full payload
 * for a negative rating and submits it to the API.
 *
 * @param {Object} data - Form data from the modal
 */
  const handleSubmitFeedback = ({ comment, expectedResponse, selectedFiles, metrics }) => {
    const message = messages[selectedMessageForFeedback];

    if (!message || !message.request_log_id) {
      logger.error("Feedback submission failed: Context lost.", { functionName: 'handleSubmitFeedback' });
      toast.error("Cannot submit feedback: Original message context lost.");
      console.error("Feedback submission failed: Could not find original message or its request_log_id.");
      handleCloseFeedbackModal();
      return;
    }

    const payload = {
      request_log_id: message.request_log_id,
      rating: 0,
      comment: comment,
      expected_response: expectedResponse,
      metrics: metrics,
      data_source: message.type || "unknown",
      attached_files: selectedFiles,
    };

    console.log(`Submitting detailed feedback for message index ${selectedMessageForFeedback}:`, payload);
    logger.info('Submitting detailed negative feedback.', {
      functionName: 'handleSubmitFeedback',
      requestLogId: message.request_log_id,
      metrics: metrics
    });

    onFeedbackSubmit(payload);
    setFeedbackState(prev => ({
      ...prev,
      [selectedMessageForFeedback]: 'dislike'
    }));
    toast.success("Thank you for your detailed feedback!");
    handleCloseFeedbackModal();
  };

  /**
* handleInsertToWord
* Parses Markdown text into structured tokens and sends them to the
* Google Apps Script backend to be inserted into the active document.
*
* @param {} markdownText - The raw markdown text
* @param {} requestLogId - ID for logging
*/
  const handleInsertToWord = async (htmlContent, requestLogId) => {
    setIsInserting(true);
    onLogCopy(requestLogId);

    logger.info('Initiating content insertion into Word.', {
      functionName: 'handleInsertToWord',
      requestLogId: requestLogId
    });

    try {
      await insertContentIntoWord(htmlContent);
      logger.info('Content successfully inserted into Word.', { functionName: 'handleInsertToWord' });
      toast.success("Content inserted into document!");
    } catch (error) {
      logger.error('Failed to insert content into Word.', {
        functionName: 'handleInsertToWord',
        originalError: error,
        requestLogId: requestLogId
      });
      toast.error(error.message);
    } finally {
      setIsInserting(false);
    }
  };
/**
 * Helper function to format database-style keys into readable headers.
 */
const formatHeader = (key) => {
 if (!key) return '';
 return key
  .split('_')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');
};
    
  const messageForFeedback = selectedMessageForFeedback !== null ? messages[selectedMessageForFeedback] : null;
  const dataSourceForModal = messageForFeedback?.type;


  // Memoized calculation to determine which source files to show in the feedback modal.
  const sourceFilesForModal = useMemo(() => {
    if (dataSourceForModal === 'file_upload') {
      const messageFiles = messageForFeedback?.fileNames;
      return (messageFiles && messageFiles.length > 0) ? messageFiles : uploadedFileNames;
    }
    return [];
  }, [dataSourceForModal, messageForFeedback, uploadedFileNames]); // Dependencies for the memo

  /**
   * Intercepts clicks on dynamically injected HTML to force anchor tags 
   * (citations/links) to open in a new tab.
   */
  const handleInlineLinkClick = (e) => {
    const aTag = e.target.closest("a");
    if (aTag && aTag.href) {
      e.preventDefault();
      window.open(aTag.href, "_blank", "noopener,noreferrer");
    }
  };

  // We loop backwards to find the index of the most recent message sent by "user"
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sender === "user") {
      lastUserIndex = i;
      break;
    }
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 main-container-area">
          {messages.map((msg, index) => {
            if (msg.type === "typing") {
              return (
                <div key={index} className="message-container bot-message-container">
                  <img src={msg.avatar} alt="Bot Avatar" className="bot-avatar" />
                  {/* Using the  AI animation classes */}
                  <div className="minimalist-typing-container">
                    <div className="minimalist-dot"></div>
                    <div className="minimalist-dot"></div>
                    <div className="minimalist-dot"></div>
                  </div>
                </div>
              );
            }
            // Loading Status (2+ mins) -> Spinner + Text
            else if (msg.type === "loading-status") {
              return (
                <div key={index} className="message-container bot-message-container">
                  <img src={msg.avatar} alt="Bot Avatar" className="bot-avatar" />
                  <div className="loading-status-container">
                    {/* Using Fluent UI Spinner */}
                    <Spinner className="loading-spinner" size="tiny" />
                    <span className="loading-status-text">{msg.text}</span>
                  </div>
                </div>
              );
            }
            else if (msg.type === "file-list") {
              return (
                <div key={index} className="system-message-wrapper">
                  <AvailableFilesMessage
                    fileNames={msg.fileList}
                    showSourceActive={msg.showSourceActive}
                    isCleared={msg.isCleared}
                  />
                </div>
              );
            }
            //  block to render the SQL table ---
            else if (msg.type === "sql-table") {
              const handleCopyTable = async () => {
                if (!msg.tableData || msg.tableData.length === 0) {
                  toast.error("No table data to copy.");
                  return;
                }
                if (!navigator.clipboard || !navigator.clipboard.write) {
                  toast.error("Formatted copy is not supported in this browser.");
                  return;
                }

                try {
                  const introText = msg.introText || '';
                  const headers = Object.keys(msg.tableData[0]).filter(header => header !== 'file_url');
                  const plainTextHeaders = headers.join('\t');
                  const plainTextRows = msg.tableData.map(row =>
                    headers.map(header => String(row[header] || '').replace(/\n/g, ' ')).join('\t')
                  ).join('\n');
                  const plainTextToCopy = `${introText}\n\n${plainTextHeaders}\n${plainTextRows}`;

                  const htmlIntro = introText ? `<p>${introText}</p>` : '';
                  const htmlHeaders = `<thead><tr>${headers.map(h => `<th>${formatHeader(h)}</th>`).join('')}</tr></thead>`;

                  const htmlRows = msg.tableData.map(row => {
                    const cells = headers.map(header => {
                      const cellValue = String(row[header] || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                      return `<td>${cellValue}</td>`;
                    }).join('');
                    return `<tr>${cells}</tr>`;
                  }).join('');

                  const htmlToCopy = `${htmlIntro}<table border="1" style="border-collapse: collapse; width: 100%;">${htmlHeaders}<tbody>${htmlRows}</tbody></table>`;

                  const htmlBlob = new Blob([htmlToCopy], { type: 'text/html' });
                  const textBlob = new Blob([plainTextToCopy], { type: 'text/plain' });

                  const clipboardItem = new ClipboardItem({
                    'text/html': htmlBlob,
                    'text/plain': textBlob,
                  });

                  await navigator.clipboard.write([clipboardItem]);

                  toast.success("Table copied with embedded links!");
                  onLogCopy(msg.request_log_id);
                } catch (error) {
                  logger.error('Failed to copy table data.', { functionName: 'handleCopyTable', originalError: error });
                  console.error("Failed to copy table data:", error);
                  toast.error("Could not copy the table data.");
                }
              };

              const handleInsertTable = async () => {
                if (!msg.tableData || msg.tableData.length === 0) {
                  toast.error("No table data to insert.");
                  return;
                }

                try {
                  //  Generate the HTML for the table (Same logic as copy)
                  const introText = msg.introText || '';
                  const headers = Object.keys(msg.tableData[0]).filter(header => header !== 'file_url');

                  const htmlIntro = introText ? `<p>${introText}</p>` : '';
                  const htmlHeaders = `<thead><tr>${headers.map(h => `<th>${formatHeader(h)}</th>`).join('')}</tr></thead>`;

                  const htmlRows = msg.tableData.map(row => {
                    const cells = headers.map(header => {
                      const cellValue = String(row[header] || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                      return `<td>${cellValue}</td>`;
                    }).join('');
                    return `<tr>${cells}</tr>`;
                  }).join('');

                  const htmlToInsert = `${htmlIntro}<table border="1" style="border-collapse: collapse; width: 100%;">${htmlHeaders}<tbody>${htmlRows}</tbody></table>`;

                  // Call  existing insertion function
                  await handleInsertToWord(htmlToInsert, msg.request_log_id);

                } catch (error) {
                  logger.error('Failed to insert table data.', { functionName: 'handleInsertTable', originalError: error });
                  console.error("Failed to insert table data:", error);
                  toast.error("Could not insert the table data into Word.");
                }
              };


              return (
                <div key={index} className="message-container bot-message-container sql-table-response DocLib-response">
                  <img src={msg.avatar} alt="Bot Avatar" className="bot-avatar" />
                  <div className="message-content-wrapper">
                    <div className="message-bubble">
                      {/* Render the SQL table component */}
                      <SqlTableMessage message={msg} />

                      {/* Render feedback actions specifically for the table */}
                      <FeedbackActions
                        onCopy={handleCopyTable}
                        onInsert={handleInsertTable}
                        onFeedback={(type) => handleFeedback(type, index)}
                        feedback={feedbackState[index]}
                        //isInserting={false}
                        isInserting={isInserting}
                        isGloballyLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>
              );
            }
            
            // General bot message check: This now ONLY runs for other bot messages
            else if (msg.sender === "bot") {
              const botHtml = parseAndSanitizeMarkdown(msg.text);
              const nonInteractiveTypes = ['welcome', 'system', 'error', 'typing', 'streaming'];
              const showFeedback = !nonInteractiveTypes.includes(msg.type);
              return (
                <div key={index} className={`message-container bot-message-container ${msg.type}-response`}>
                  <img src={msg.avatar} alt="Bot Avatar" className="bot-avatar" />
                  <div className="message-content-wrapper">
                    <div className="message-bubble">
                      <div onClick={handleInlineLinkClick} dangerouslySetInnerHTML={{ __html: botHtml }}></div>
                      {/* Conditionally render the suggestions */}
                      <GoogleSuggestions links={msg.carouselLinks} />
                      {/* Render Document Sources if they exist */}
                      <DocumentSources fileNames={msg.fileNames} urls={msg.urls} pageNumbers={msg.pageNumbers} />
                      {/*
                            The Feedback Actions, now INSIDE the bubble.
                            This will only render if 'showFeedback' is true, which means
                            the message is complete and is not a system/welcome/error message.
                          */}
                      {showFeedback && (
                        <FeedbackActions
                          // isCopied={isCopied}
                          onCopy={() => handleCopy(botHtml, msg.request_log_id)}
                          onFeedback={(type) => handleFeedback(type, index)}
                          feedback={feedbackState[index]}
                          onInsert={() => handleInsertToWord(botHtml, msg.request_log_id)} // Pass the handler
                          isInserting={isInserting} // Pass the loading state
                          isGloballyLoading={isLoading}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            // Final check for user messages
            else if (msg.sender === "user") {
              const isLatestUserMessage = index === lastUserIndex;
              return (
                // Use msg.id as the key for stable rendering
                <div key={msg.id || index} className="message-container user-message-container">
                  {/* This wrapper groups the bubble and actions together */}
                  <div className="message-bubble">
                    {/* This div holds the user's text */}
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                    {/* Render the new BookmarkAction component */}
                    <BookmarkAction
                      isBookmarked={!!msg.isBookmarked}
                      onToggle={() => onToggleBookmark(msg.request_log_id)}
                      isGloballyLoading={isLoading || msg.isBookmarkLoading}
                      errorStatus={msg.errorStatus}
                      onRetry={() => onRetry(msg.id)}
                      isLatest={isLatestUserMessage}
                    />
                  </div>

                  <div className="user-avatar">
                    <Avatar
                      className="my-custom-user-avatar"
                      name={msg.user?.displayName || "User"}
                      image={msg.user?.avatar ? { src: msg.user.avatar } : undefined}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
      <FeedbackModal
        key={feedbackModalKey}
        isOpen={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedback}
        sourceFiles={sourceFilesForModal}
        dataSource={dataSourceForModal}
        sessionId={sessionId}
      />
    </div>
  );
};

Main.propTypes = {
  messages: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  uploadedFileNames: PropTypes.arrayOf(PropTypes.string),
  onFeedbackSubmit: PropTypes.func.isRequired,
  sessionId: PropTypes.string.isRequired,
  onLogCopy: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default Main;